// Deutsche Fehlermeldungen für Supabase-Auth (GoTrue).
//
// Supabase liefert englische Roh-Texte (z. B. "Invalid login credentials") und
// optional einen stabilen `code` (z. B. "invalid_credentials"). Wir mappen
// bevorzugt über den Code und fallen auf Textmuster zurück, damit die UI
// einheitlich deutsch bleibt. Technische Details landen weiterhin in der Konsole.

const AUTH_FALLBACK = "Anmeldung fehlgeschlagen. Bitte erneut versuchen.";

// Stabile GoTrue-Fehlercodes → deutsche Meldung.
const AUTH_ERROR_BY_CODE: Record<string, string> = {
  invalid_credentials: "Anmeldedaten ungültig. Bitte E-Mail und Passwort prüfen.",
  email_not_confirmed:
    "E-Mail-Adresse ist noch nicht bestätigt. Bitte zuerst den Bestätigungslink öffnen.",
  user_already_exists: "Diese E-Mail-Adresse ist bereits registriert.",
  email_exists: "Diese E-Mail-Adresse ist bereits registriert.",
  user_not_found: "Kein Konto mit diesen Daten gefunden.",
  weak_password: "Das Passwort ist zu schwach. Bitte ein längeres Passwort wählen.",
  over_request_rate_limit:
    "Zu viele Versuche. Bitte einen Moment warten und erneut versuchen.",
  over_email_send_rate_limit:
    "Zu viele E-Mails angefordert. Bitte später erneut versuchen.",
  email_address_invalid: "Die E-Mail-Adresse ist ungültig.",
  validation_failed: "Eingabe ungültig. Bitte die Felder prüfen.",
  signup_disabled: "Registrierung ist derzeit deaktiviert.",
  session_not_found: "Die Sitzung ist abgelaufen. Bitte erneut anmelden.",
  session_expired: "Die Sitzung ist abgelaufen. Bitte erneut anmelden.",
};

// Fallback: Textmuster für ältere/codelose GoTrue-Antworten und Netzwerkfehler.
const AUTH_ERROR_BY_MESSAGE: Array<[RegExp, string]> = [
  [/invalid login credentials/i, AUTH_ERROR_BY_CODE.invalid_credentials],
  [/email not confirmed/i, AUTH_ERROR_BY_CODE.email_not_confirmed],
  [
    /already registered|already been registered|user already exists/i,
    AUTH_ERROR_BY_CODE.user_already_exists,
  ],
  [/password should be at least/i, AUTH_ERROR_BY_CODE.weak_password],
  [
    /rate limit|too many requests|after \d+ seconds/i,
    AUTH_ERROR_BY_CODE.over_request_rate_limit,
  ],
  [/signups? not allowed|signup is disabled/i, AUTH_ERROR_BY_CODE.signup_disabled],
  [
    /invalid (email|format)|unable to validate email/i,
    AUTH_ERROR_BY_CODE.email_address_invalid,
  ],
  [
    /network|failed to fetch|load failed|fetch failed/i,
    "Verbindung zum Server fehlgeschlagen. Bitte Internetverbindung prüfen.",
  ],
];

/** Übersetzt einen Supabase-Auth-Fehler in eine deutsche Nutzermeldung. */
export function authErrorMessage(error: unknown, fallback: string = AUTH_FALLBACK): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: unknown }).code ?? "")
      : "";
  if (code && AUTH_ERROR_BY_CODE[code]) {
    return AUTH_ERROR_BY_CODE[code];
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  for (const [pattern, label] of AUTH_ERROR_BY_MESSAGE) {
    if (pattern.test(message)) {
      return label;
    }
  }
  return fallback;
}
